import React from 'react';
import CountUp from 'react-countup';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { MarketButton } from '@market/react';
import { toCurrency } from 'utilities/currency';
import { getLocalePath, localizedShopPath } from 'utilities/locales';
import { trackCdp } from 'utilities/tracking';
import CDP_EVENT_NAMES from 'constants/cdp_events';
import { trackContinueToCart } from './helpers';
import { getSoftwarePageViewed } from 'utilities/cookies';

const SubtotalBox = ({
  additionalText = [],
  callToAction,
  cart,
  customFormatting = toCurrency,
  customTitle,
  decimals = 0,
  itemCount,
  product,
  saasEligibility = {},
  subtotal,
  customSubtotal,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const softwarePageViewed = getSoftwarePageViewed();

  const translateFreeShippingQuote = (quote) => {
    const timeUnit = t(
      `shipping.shippingEstimate.timeUnits.${quote.time_unit}.other`,
    );
    const est = t('productPage.shippingEstimate', {
      min: quote.min,
      max: quote.max,
      timeUnit,
    });
    return t('productPage.freeShippingEstimate', { est });
  };

  const translateExpeditedShippingQuote = (quote) => {
    const timeUnit = t(
      `shipping.shippingEstimate.timeUnits.${quote.time_unit}.other`,
    );
    const est = t('productPage.shippingEstimate', {
      min: quote.min,
      max: quote.max,
      timeUnit,
    });
    return t('productPage.expeditedShippingEstimate', { est });
  };

  return (
    <Box
      sx={{
        textAlign: 'center',
        bgcolor: 'grey.100',
        borderRadius: 3,
        px: 3,
        py: 4,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <p className="medium-30">
          {customTitle || t('productPage.cartSubtotal')}
        </p>
        {customSubtotal ? (
          <p className="display-10">{customSubtotal}</p>
        ) : (
          <CountUp
            preserveValue
            decimals={decimals}
            duration={0.3}
            end={subtotal}
            formattingFn={customFormatting}
            className="display-10"
            style={{
              margin: theme.spacing(1, 0),
              fontVariantNumeric: 'tabular-nums',
            }}
          />
        )}

        {product.financing.product_financeable && (
          <p className="paragraph-10" style={{ color: theme.color.gray['55'] }}>
            {t('productPage.continueToSeeFinance')}
          </p>
        )}
      </Box>
      {additionalText.length > 0 && <Box sx={{ mb: 3 }}>{additionalText}</Box>}
      <Box sx={{ mb: 3 }}>
        {/**
         * If the callToAction component prop is given, render the CTA otherwise
         * render the default continue to checkout / continue shopping buttons.
         */}
        {callToAction ?? (
          <>
            <MarketButton
              {...(itemCount === 0 && { disabled: true })}
              id="continue-to-cart-btn"
              rank="primary"
              href={`${
                saasEligibility?.eligible && !softwarePageViewed
                  ? `${localizedShopPath('v2')}/software`
                  : `${localizedShopPath()}/cart`
              }`}
              onClick={() => {
                trackContinueToCart({
                  orderNumber: cart.number,
                  trackingData: product.tracking_data,
                });

                trackCdp({
                  name: CDP_EVENT_NAMES.SAAS_ON_SHOP.CONTINUE_TO_CART,
                  params: {
                    feature: 'shop',
                    order_number: cart.number,
                    software_page_viewed: softwarePageViewed,
                    saas_eligible: saasEligibility?.eligible,
                  },
                });
              }}
              style={{ width: '100%' }}
            >
              {t('productPage.continueToCart')}
            </MarketButton>
            <MarketButton
              id="continue-shopping-btn"
              href={`${getLocalePath().toLowerCase()}/hardware`}
              style={{
                backgroundColor: 'white',
                border: '1.5px solid #E5E5E5',
                marginTop: theme.spacing(2),
                width: '100%',
              }}
            >
              {t('productPage.continueShopping')}
            </MarketButton>
          </>
        )}
      </Box>
      {!product.shipping.no_ship && (
        <Box sx={{ color: theme.color.gray['55'] }}>
          <p
            className="paragraph-10"
            style={{ marginBottom: theme.spacing(0.5) }}
          >
            {translateFreeShippingQuote(product.shipping.ship_quote)}
          </p>
          {product.shipping.expedited_available && (
            <p
              className="paragraph-10"
              style={{ marginBottom: theme.spacing(0.5) }}
            >
              {translateExpeditedShippingQuote(
                product.shipping.ship_quote.expedited,
              )}
            </p>
          )}
          <p
            className="paragraph-10"
            style={{ marginBottom: theme.spacing(0.5) }}
          >
            {t('productPage.freeReturns', { returnWindow: cart.return_window })}
          </p>
        </Box>
      )}
    </Box>
  );
};

export default SubtotalBox;
